import { cn } from '@/lib/utils';
import {
    ArrowRight,
    Check,
    ChevronLeft,
    ChevronRight,
    Pencil,
    X
} from 'lucide-react';
import { Fragment, useEffect, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import { FluidHoverGroup } from '@/components/ui/fluid-hover-group';
import { FluidHoverDivRow } from '@/components/ui/fluid-hover-row';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { useTranslation } from 'components/i18n/Translator';

export interface DockOption {
    id: string;
    title: string;
    description?: string;
}

export interface DockQuestion {
    id: string;
    title: string;
    options: DockOption[];
    multiSelect?: boolean;
}

export interface DockAnswer {
    questionId: string;
    selectedIds: string[];
    otherText?: string;
    skipped?: boolean;
}

interface Props {
    questions: DockQuestion[];
    onComplete: (answers: DockAnswer[]) => void;
    onDismiss: () => void;
}

export default function AskUserQuestionsDock({
    questions,
    onComplete,
    onDismiss
}: Props) {
    const { t } = useTranslation();
    const [index, setIndex] = useState(0);
    const [answers, setAnswers] = useState<Record<string, DockAnswer>>({});
    const [picked, setPicked] = useState<string[]>([]);
    const [otherText, setOtherText] = useState('');

    const contentRef = useRef<HTMLDivElement>(null);
    const [contentHeight, setContentHeight] = useState<number>();

    useEffect(() => {
        const content = contentRef.current;
        if (!content) return;
        const observer = new ResizeObserver(() =>
            setContentHeight(content.offsetHeight)
        );
        observer.observe(content);
        return () => observer.disconnect();
    }, []);

    const question = questions[index];
    const isLast = index === questions.length - 1;

    const restore = (
        target: number,
        source: Record<string, DockAnswer> = answers
    ) => {
        const saved = source[questions[target].id];
        setPicked(saved?.selectedIds ?? []);
        setOtherText(saved?.otherText ?? '');
    };

    const commit = (answer: DockAnswer) => {
        const next = { ...answers, [answer.questionId]: answer };
        setAnswers(next);
        if (isLast) {
            onComplete(
                questions.map(
                    (q) =>
                        next[q.id] ?? {
                            questionId: q.id,
                            selectedIds: [],
                            skipped: true
                        }
                )
            );
            return;
        }
        setIndex(index + 1);
        restore(index + 1);
    };

    const choose = (optionId: string) => {
        if (!question.multiSelect) {
            commit({ questionId: question.id, selectedIds: [optionId] });
            return;
        }
        setPicked((current) =>
            current.includes(optionId)
                ? current.filter((id) => id !== optionId)
                : [...current, optionId]
        );
    };

    const submitOther = () => {
        const text = otherText.trim();
        if (!text) return;
        commit({
            questionId: question.id,
            selectedIds: question.multiSelect ? picked : [],
            otherText: text
        });
    };

    const skip = () =>
        commit({ questionId: question.id, selectedIds: [], skipped: true });

    const canContinue = picked.length > 0 || otherText.trim() !== '';

    const continueMulti = () =>
        otherText.trim()
            ? submitOther()
            : commit({ questionId: question.id, selectedIds: picked });

    const go = (delta: number) => {
        const target = index + delta;
        if (target < 0 || target >= questions.length) return;
        const hasDraft = picked.length > 0 || otherText.trim() !== '';
        const next = hasDraft
            ? {
                  ...answers,
                  [question.id]: {
                      questionId: question.id,
                      selectedIds: picked,
                      otherText: otherText.trim() || undefined
                  }
              }
            : answers;
        setAnswers(next);
        setIndex(target);
        restore(target, next);
    };

    useEffect(() => {
        const onKey = (event: KeyboardEvent) => {
            const inDockOrIdle =
                event.target === document.body ||
                contentRef.current?.contains(event.target as Node);
            if (
                question.multiSelect &&
                inDockOrIdle &&
                event.key === 'Enter' &&
                (event.ctrlKey || event.metaKey)
            ) {
                event.preventDefault();
                if (canContinue) continueMulti();
                return;
            }
            const target = event.target as HTMLElement;
            if (target.closest('input, textarea, [contenteditable]')) return;
            const number = Number(event.key);
            if (number >= 1 && number <= question.options.length) {
                choose(question.options[number - 1].id);
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    });

    return (
        <div className="bg-white dark:bg-sidebar-accent composer-shadow mb-2 px-4 pt-4 pb-3 border rounded-xl">
            <div
                className="overflow-hidden transition-[height] motion-reduce:transition-none duration-200 ease-out"
                style={{ height: contentHeight }}
            >
                <div ref={contentRef}>
                    <div className="flex justify-between items-center gap-4 mb-3">
                        <div className="font-medium text-title">
                            {question.title}
                        </div>
                        <div className="flex items-center gap-2 text-muted-foreground text-sm shrink-0">
                            <Button
                                variant="ghost"
                                size="icon-sm"
                                className="text-muted-foreground hover:text-foreground"
                                disabled={index === 0}
                                onClick={() => go(-1)}
                            >
                                <ChevronLeft className="size-4" />
                            </Button>
                            <span>
                                {t('chat.askUser.progress', {
                                    current: index + 1,
                                    total: questions.length
                                })}
                            </span>
                            <Button
                                variant="ghost"
                                size="icon-sm"
                                className="text-muted-foreground hover:text-foreground"
                                disabled={isLast}
                                onClick={() => go(1)}
                            >
                                <ChevronRight className="size-4" />
                            </Button>
                            <Button
                                variant="ghost"
                                size="icon-sm"
                                onClick={onDismiss}
                                className="ml-2 text-muted-foreground hover:text-foreground"
                            >
                                <X className="size-4" />
                            </Button>
                        </div>
                    </div>
                    <FluidHoverGroup className="group/dock flex flex-col">
                        {question.options.map((option, i) => {
                            const selected = picked.includes(option.id);
                            return (
                                <Fragment key={option.id}>
                                    {i > 0 ? (
                                        <Separator className="[&:has(+*:hover)]:opacity-0 [button:hover+&]:opacity-0 transition-opacity" />
                                    ) : null}
                                    <Button
                                        variant="bare"
                                        onClick={() => choose(option.id)}
                                        className="group/option justify-start gap-3 group-has-[input:focus]/dock:opacity-40 px-2 py-2 w-full h-auto font-normal text-left whitespace-normal transition-opacity"
                                    >
                                        <span
                                            className={cn(
                                                'relative place-items-center grid rounded-md size-7 text-caption transition-colors shrink-0',
                                                'bg-accent text-muted-foreground group-hover/option:bg-muted group-hover/option:text-foreground',
                                                selected &&
                                                    'bg-foreground text-background group-hover/option:bg-foreground group-hover/option:text-background'
                                            )}
                                        >
                                            {selected ? <Check /> : i + 1}
                                            {question.multiSelect ? null : (
                                                <span className="absolute inset-0 place-items-center grid bg-foreground opacity-0 group-hover/option:opacity-100 rounded-md text-background transition-opacity">
                                                    <ArrowRight className="size-4" />
                                                </span>
                                            )}
                                        </span>
                                        <span className="flex flex-col flex-1 min-w-0">
                                            <span className="font-medium text-foreground text-subtitle group-hover/option:text-foreground transition-colors">
                                                {option.title}
                                            </span>
                                            {option.description ? (
                                                <span className="text-caption text-muted-foreground group-hover/option:text-muted-foreground transition-colors">
                                                    {option.description}
                                                </span>
                                            ) : null}
                                        </span>
                                    </Button>
                                </Fragment>
                            );
                        })}
                        <Separator className="[&:has(+*:hover)]:opacity-0 [button:hover+&]:opacity-0 transition-opacity" />
                        <FluidHoverDivRow className="group/other flex items-center gap-3 focus-within:bg-accent px-2 py-2 rounded-lg transition-colors">
                            <span className="place-items-center grid bg-accent group-focus-within/other:bg-muted group-hover/other:bg-muted rounded-md size-7 transition-colors shrink-0">
                                <Pencil className="size-3.5 text-muted-foreground group-focus-within/other:text-foreground group-hover/other:text-foreground transition-colors" />
                            </span>
                            <Input
                                key={question.id}
                                value={otherText}
                                onChange={(event) =>
                                    setOtherText(event.target.value)
                                }
                                onKeyDown={(event) => {
                                    if (
                                        event.key === 'Enter' &&
                                        !event.nativeEvent.isComposing
                                    )
                                        submitOther();
                                }}
                                placeholder={t('chat.askUser.other')}
                                className="flex-1 bg-transparent dark:bg-transparent p-0 border-0 rounded-none focus-visible:ring-0 h-5 text-foreground placeholder:text-muted-foreground/70 group-hover/other:placeholder:text-muted-foreground"
                            />
                            <Button
                                variant="ghost"
                                size="default"
                                onClick={skip}
                                className={cn(
                                    'text-muted-foreground hover:text-foreground',
                                    !question.multiSelect &&
                                        'group-focus-within/other:hidden'
                                )}
                            >
                                {t('chat.askUser.skip')}
                                <ArrowRight className="size-4" />
                            </Button>
                            {question.multiSelect ? (
                                <Button
                                    size="default"
                                    disabled={!canContinue}
                                    onClick={continueMulti}
                                >
                                    {isLast
                                        ? t('chat.askUser.send')
                                        : t('chat.askUser.next')}
                                </Button>
                            ) : (
                                <Button
                                    size="icon-sm"
                                    aria-label={t('chat.askUser.send')}
                                    disabled={!otherText.trim()}
                                    onClick={submitOther}
                                    className="hidden group-focus-within/other:inline-flex"
                                >
                                    <ArrowRight />
                                </Button>
                            )}
                        </FluidHoverDivRow>
                    </FluidHoverGroup>
                </div>
            </div>
        </div>
    );
}
